import { FormControl, FormErrorMessage, FormLabel } from '@chakra-ui/react'
import { appColors } from '@theme/foundations/colors'
import React from 'react'
import Select, { components, type Props as SelectProps, type StylesConfig } from 'react-select'
import { BaseTooltip } from '../Tooltip'
import { FlexRow } from '../flex'

interface IBaseSelectProps extends SelectProps {
  isInvalid: boolean
  errorMessage?: string
  label: string
  labelFontSize?: string
  labelColor?: string
  labelFontWeight?: string
  showInfo?: boolean
  helperText?: string
  height?: string
  width?: string
  fontSize?: string
  fontWeight?: number
  letterSpacing?: string
  unfocusedBorderColor?: string
  borderWidth?: number
  fontColor?: string
  controlProps?: any
  borderRadius?: string
  anchorMenuToDocumentBody?: boolean
  backgroundColor?: string
  dataTestId?: string
  leftIcon?: React.ReactNode
}

const customStyles = ({
  isInvalid,
  width,
  height,
  fontSize,
  fontWeight,
  letterSpacing,
  unfocusedBorderColor,
  borderWidth,
  fontColor,
  controlProps,
  borderRadius,
  anchorMenuToDocumentBody,
  backgroundColor,
  hasLeftIcon,
}: {
  isInvalid: boolean
  width?: string
  height?: string
  fontSize?: string
  fontWeight?: number
  letterSpacing?: string
  unfocusedBorderColor?: string
  borderWidth?: number
  fontColor?: string
  controlProps?: any
  borderRadius?: string
  anchorMenuToDocumentBody?: boolean
  backgroundColor?: string
  hasLeftIcon?: boolean
}): StylesConfig => ({
  option: (_, state) => ({
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    color: state.isDisabled ? appColors.gray[400] : state.isSelected ? appColors.primaryForeground : appColors.foreground,
    fontSize: '14px',
    fontWeight: '400',
    backgroundColor: state.isSelected ? appColors.accentPrimary : appColors.background,
    minHeight: '40px',
    overflowWrap: 'anywhere',
    borderRadius: '8px',
    margin: '2px 4px',
    ':hover': {
      backgroundColor: state.isDisabled ? appColors.background : state.isSelected ? appColors.accentPrimary : appColors.muted,
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    },
    padding: '8px 12px',
    border: 'none',
    opacity: state.isDisabled ? 0.6 : 1,
  }),
  control: (_, state) => {
    const boxShadow = state.isFocused
      ? '0px 10px 12px -3px rgba(0, 0, 0, 0.02), 0px 4px 2px -2px rgba(0, 0, 0, 0.02)'
      : isInvalid
        ? '0px 10px 12px -3px rgba(0, 0, 0, 0.02), 0px 4px 2px -2px rgba(0, 0, 0, 0.02)'
        : 'none'

    const borderColor = state.isFocused ? appColors.primary : isInvalid ? appColors.red[500] : appColors.border

    return {
      color: fontColor,
      fontSize: fontSize ? fontSize : '16px',
      fontWeight: fontWeight ? fontWeight : undefined,
      letterSpacing: letterSpacing ? letterSpacing : undefined,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: hasLeftIcon ? '12px' : '16px',
      height: height ? height : '32px',
      width: width ? width : undefined,
      border: '1px solid',
      display: 'flex',
      boxShadow,
      borderColor,
      borderWidth,
      borderRadius: borderRadius ?? '8px',
      backgroundColor,
      ...controlProps,
    }
  },
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.5 : 1
    const transition = 'opacity 300ms'

    return { ...provided, opacity, transition, color: fontColor }
  },
  dropdownIndicator: (provided, state) => {
    const transform = state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'none'
    const transition = 'transform 0.2s ease-in-out'
    return {
      ...provided,
      transform,
      transition,
      color: state.isDisabled ? appColors.gray[400] : fontColor ?? appColors.foreground,
    }
  },
  valueContainer: provided => ({
    ...provided,
    flex: 1,
    width: 'auto',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    margin: 0,
    padding: '0',
  }),
  input: () => ({
    position: 'absolute',
  }),
  menu: () => ({
    top: anchorMenuToDocumentBody ? '15px' : '45px',
    bottom: '0',
    width: width ?? '100%',
    position: 'absolute',
    backgroundColor: '#FFF',
    border: '1px solid #ECECEC',
    borderRadius: '8px',
    boxShadow: '0px 10px 12px -3px rgba(0, 0, 0, 0.02), 0px 4px 2px -2px rgba(0, 0, 0, 0.02)',
    zIndex: 999,
    height: 'fit-content',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  placeholder: (provided, state) => {
    const top = state.hasValue || state.selectProps.inputValue || state.isFocused ? 6 : '30%'

    const fontSize = state.hasValue || state.selectProps.inputValue || state.isFocused ? 13 : provided.fontSize
    const color = state.hasValue || state.selectProps.inputValue || state.isFocused ? appColors.gray[500] : fontColor ?? appColors.gray[400]

    return {
      ...provided,
      position: 'absolute',
      transition: 'top 0.1s, font-size 0.1s',
      color,
      top,
      fontSize,
      maxWidth: '90%',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    }
  },
})

const CustomValueContainer = ({ children, ...props }: any) => {
  return (
    <components.ValueContainer {...props}>
      {/* <Placeholder {...props} isFocused={props.isFocused}>
        {props.selectProps.placeholder}
      </Placeholder> */}
      {React.Children.map(children, child => (child && child.type !== components.Placeholder ? child : null))}
    </components.ValueContainer>
  )
}

const makeCustomControl =
  (leftIcon?: React.ReactNode) =>
  ({ children, ...props }: any) => {
    return (
      <components.Control {...props}>
        {leftIcon ? (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              marginRight: '8px',
              color: appColors.foreground,
            }}
          >
            {leftIcon}
          </span>
        ) : null}
        {children}
      </components.Control>
    )
  }

export const BaseSelect = ({
  isInvalid,
  onChange,
  isMulti,
  value,
  options,
  errorMessage,
  label,
  showInfo,
  helperText,
  width,
  height,
  fontSize,
  fontWeight,
  letterSpacing,
  unfocusedBorderColor,
  borderWidth,
  components,
  controlProps,
  fontColor,
  labelFontSize,
  labelColor,
  labelFontWeight,
  borderRadius,
  anchorMenuToDocumentBody,
  backgroundColor,
  leftIcon,
  ...props
}: IBaseSelectProps) => {
  const styles = customStyles({
    isInvalid,
    width,
    height,
    fontSize,
    fontWeight,
    letterSpacing,
    unfocusedBorderColor,
    borderWidth,
    controlProps,
    fontColor,
    borderRadius,
    anchorMenuToDocumentBody,
    backgroundColor,
    hasLeftIcon: !!leftIcon,
  })

  return (
    <FormControl isInvalid={isInvalid} data-testid={props.dataTestId}>
      {(label || showInfo) && (
        <FlexRow justifyContent='flex-start' marginBottom={2} alignItems={'center'}>
          <FormLabel
            aria-label={label}
            fontWeight={labelFontWeight}
            color={labelColor ? labelColor : undefined}
            fontSize={labelFontSize ? labelFontSize : undefined}
            marginBottom={0}
          >
            {label}
          </FormLabel>
          {showInfo && <BaseTooltip width={'16px'} height={'16px'} label={helperText} />}
        </FlexRow>
      )}
      <Select
        styles={styles}
        menuPortalTarget={anchorMenuToDocumentBody ? document.body : undefined}
        {...props}
        onChange={(val: any, actionMeta) => {
          isMulti
            ? onChange?.(
                val.map(c => c.value),
                actionMeta
              )
            : onChange?.(val.value, actionMeta)
        }}
        value={isMulti ? options?.filter((c: any) => (value as any).includes(c.value)) : options?.find((c: any) => c.value === value)}
        isMulti={isMulti}
        options={options}
        components={{
          Control: makeCustomControl(leftIcon),
          ValueContainer: CustomValueContainer,
          ...components,
        }}
      />
      {isInvalid && errorMessage ? (
        <FormErrorMessage position='absolute' bottom='-6' top='auto' fontSize='12px'>
          {errorMessage}
        </FormErrorMessage>
      ) : null}
    </FormControl>
  )
}
